// components/widget/WidgetNavigationButtons.tsx
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from '../../common/constants/ViewEnum';
import { trackEvent } from '../../common/utils/analytics';
import PrimaryButton from '../buttons/PrimaryButton';
import PrimaryButtonDark from '../buttons/PrimaryButtonDark';
import SecondaryButton from '../buttons/SecondaryButton';
import PrimaryParagraph from '../texts/PrimaryParagraph';

interface WidgetNavigationButtonsProps {
	step: number;
	selectedBrandsLength?: number;
	isAuthenticated: boolean;
	isOnboarded: boolean;
	handleBackStep: () => void;
	handleNextFromBrandView?: () => void;
	handleSkipProduct?: () => void;
	toggleView?: (view: string) => void;
  isTypeSelection?: boolean;
  isAddItemFlow?: boolean;
}

const WidgetNavigationButtons: FC<WidgetNavigationButtonsProps> = ({
	step,
	selectedBrandsLength = 0,
	isAuthenticated,
	isOnboarded,
	handleBackStep,
	handleNextFromBrandView,
	handleSkipProduct,
	toggleView,
  isTypeSelection = false,
  isAddItemFlow = false
}) => {
	const { t } = useTranslation();

	// Helper to get current screen name based on step
	const getCurrentScreenName = (currentStep: number) => {
		switch (currentStep) {
			case 4: return 'brandselectionview';
			case 5: return 'productselectionview';
			case 6: return 'sizeselectionview';
			default: return 'register';
		}
	};

	// Log navigation context
	if (isAddItemFlow) {
		console.debug(`🎛️ [ADD_ITEM] Navigation buttons for step ${step} (Add Item flow)`);
	}

	if (step < 1 || step > 6) return null;

	return (
		<div className="pop-sticky pop-flex pop-flex-col z-10 pop-w-full pop-h-full pop-justify-between pop-bg-popsizeSecondary10">
      <div className="pop-w-full">
			{step === 4 && (
				<div className="pop-flex pop-flex-row pop-items-start pop-gap-4 pop-justify-between">
					<PrimaryButton onClick={() => { trackEvent('back_button_clicked', { screenName: getCurrentScreenName(step) }); handleBackStep(); }}>{t('back')}</PrimaryButton>
					<SecondaryButton
						onClick={handleNextFromBrandView}
						isDisabled={selectedBrandsLength < 1}
						className="pop-bg-popsizeGradientSecondary9050 pop-text-popsizeSecondary10 pop-flex-1 pop-w-full"
					>
						{t('next')}
					</SecondaryButton>
				</div>
			)}

			{step === 5 && !isTypeSelection && (
        <div className="pop-flex pop-flex-row pop-items-start pop-gap-4 pop-justify-between">
					<PrimaryButton onClick={() => { trackEvent('back_button_clicked', { screenName: getCurrentScreenName(step) }); handleBackStep(); }}>{t('back')}</PrimaryButton>
					<PrimaryButtonDark
						onClick={handleSkipProduct}
						isDisabled={selectedBrandsLength < 1}
						//className="pop-bg-popsizeGradientPrimary9050 pop-text-popsizePrimary10 pop-flex-1 pop-w-full"
					>
						{t('no_product_button')}
					</PrimaryButtonDark>
				</div>
			)}
      {step === 5 && isTypeSelection && (
				<div className="pop-flex pop-flex-row pop-items-start pop-gap-4 pop-justify-between">
					<PrimaryButton onClick={() => { trackEvent('back_button_clicked', { screenName: getCurrentScreenName(step) }); handleBackStep(); }}>{t('back')}</PrimaryButton>
					<SecondaryButton
						onClick={handleNextFromBrandView}
						isDisabled={selectedBrandsLength < 1}
						className="pop-bg-popsizeGradientSecondary9050 pop-text-popsizeSecondary10 pop-flex-1 pop-w-full"
					>
						{t('next')}
					</SecondaryButton>
				</div>
			)}
      {step === 6 && (
        <div className="pop-flex pop-flex-row pop-items-start pop-gap-4 pop-justify-between">
					<PrimaryButton onClick={() => { trackEvent('back_button_clicked', { screenName: getCurrentScreenName(step) }); handleBackStep(); }} fullButtonPrimary={true}>{t('back')}</PrimaryButton>
				</div>
			)}
      </div>
      <div className="pop-flex pop-w-full pop-justify-center pop-items-center">
        {!isAuthenticated && !isOnboarded && (
          <PrimaryParagraph className="pop-text-center">
            {t('already_have_account')}{' '}
            <span
              className="pop-text-popsizeSecondary90 pop-cursor-pointer hover:pop-underline"
              onClick={() => toggleView?.(View.Login)}
            >
              {t('sign_in')}
            </span>
          </PrimaryParagraph>
        )}
      </div>
    </div>
	);
};

export default WidgetNavigationButtons;
